import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";

import CoverageBars from "../../components/director/module1/CoverageBars";
import MetricCard from "../../components/director/module1/MetricCard";
import ModuleShell from "../../components/director/module1/ModuleShell";
import ModuleState from "../../components/director/module1/ModuleState";
import useDirectorSurface from "../../components/director/module1/useDirectorSurface";
import { useDirectorContext } from "@contexts/director";
import {
  compactCents,
  formatBps,
  formatCompetenceShort,
  formatFreshness,
  sumKnownCents,
} from "../../utils/directorFormat";
import { companyQuery, currentCompetence, oldestExtractedAt } from "./module1Utils";
import useProjectRoute from "./useProjectRoute";

const BASE_PATH = "/dashboard/director/receivables-commitments";

const monthOrNone = (value, none = "Nenhum") => (value ? formatCompetenceShort(value) : none);

const commitmentsOf = (row = {}) => sumKnownCents(row.committedCents, row.plannedCents);

const Kpis = ({ summary = {}, horizonMonths, project }) => {
  const commitments = commitmentsOf(summary);
  const worst = summary.worstCumulativeCents;
  const hasGap = Number.isSafeInteger(worst) && worst < 0;
  const months = horizonMonths || 12;
  return <section className="director-module1__metrics director-module1__metrics--four" aria-label="Indicadores de cobertura">
    <MetricCard
      label={`Entradas previstas · ${months} meses`}
      value={compactCents(summary.receivableCents)}
      note="Parcelas da carteira e outras receitas do ERP"
      tone="blue"
    />
    <MetricCard
      label={`Compromissos · ${months} meses`}
      value={compactCents(commitments)}
      note={`Contratados ${compactCents(summary.committedCents)} · planejados ${compactCents(summary.plannedCents)}`}
      tone="orange"
    />
    <MetricCard
      label="Cobertura"
      value={formatBps(summary.coverageBps)}
      note={project
        ? `${Number.isSafeInteger(summary.uncoveredMonths) ? summary.uncoveredMonths : 0} de ${months} meses descobertos`
        : "Entradas previstas ÷ compromissos"}
      tone={Number.isSafeInteger(summary.coverageBps) && summary.coverageBps < 10000 ? "pressure" : "green"}
    />
    <MetricCard
      label={project ? "Falta acumulada" : "Maior descoberto acumulado"}
      value={hasGap ? compactCents(Math.abs(worst)) : "Sem descoberto"}
      note={hasGap
        ? `${project ? "Até" : "Em"} ${monthOrNone(summary.worstCumulativeMonth, "mês não informado")}, sem contar saldo em caixa`
        : "O acumulado não fica negativo, sem contar saldo em caixa"}
      tone={hasGap ? "pressure" : "neutral"}
    />
  </section>;
};

const ProjectsTable = ({ data, selectedCompanyId }) => {
  const projects = data.projects || [];
  const summary = data.summary || {};
  return <article className="director-module1__panel">
    <header>
      <div>
        <span className="director-eyebrow">Por obra</span>
        <h2>Quem se paga e quem precisa de aporte</h2>
      </div>
    </header>
    <div className="director-table-scroll">
      <table className="director-table director-owner-table" aria-label="Cobertura por obra">
        <thead>
          <tr>
            <th scope="col">Obra</th>
            <th scope="col">Entradas previstas</th>
            <th scope="col">Compromissos</th>
            <th scope="col">Cobertura</th>
            <th scope="col">Primeiro mês descoberto</th>
          </tr>
        </thead>
        <tbody>
          {projects.map((project) => <tr key={project.projectKey} data-uncovered={project.uncoveredMonths > 0 ? "true" : "false"}>
            <th scope="row">
              <Link to={`${BASE_PATH}/${encodeURIComponent(project.projectKey)}${companyQuery(selectedCompanyId)}`}>
                {project.name || "Obra sem nome"}
              </Link>
            </th>
            <td>{compactCents(project.receivableCents)}</td>
            <td>{compactCents(commitmentsOf(project))}</td>
            <td>{formatBps(project.coverageBps)}</td>
            <td>{monthOrNone(project.firstUncoveredMonth)}</td>
          </tr>)}
          {data.shared && <tr>
            <th scope="row">Sem obra (compartilhado)</th>
            <td>{compactCents(data.shared.receivableCents)}</td>
            <td>{compactCents(commitmentsOf(data.shared))}</td>
            <td>—</td>
            <td>—</td>
          </tr>}
        </tbody>
        <tfoot>
          <tr>
            <th scope="row">Portfólio</th>
            <td>{compactCents(summary.receivableCents)}</td>
            <td>{compactCents(commitmentsOf(summary))}</td>
            <td>{formatBps(summary.coverageBps)}</td>
            <td>{monthOrNone(summary.firstUncoveredMonth)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
    <p className="director-owner-footnote">Clique numa obra para ver o mesmo gráfico só dela.</p>
  </article>;
};

const ReceivablesCommitments = () => {
  const { project_key: routeProjectKey } = useParams();
  const projectKey = routeProjectKey || null;
  const { selectedCompanyId, accessByCompany = {}, metadata = {} } = useDirectorContext();
  const { clearProject } = useProjectRoute({
    routeKey: projectKey,
    pathFor: (key) => (key
      ? `${BASE_PATH}/${encodeURIComponent(key)}${companyQuery(selectedCompanyId)}`
      : `${BASE_PATH}${companyQuery(selectedCompanyId)}`),
  });
  const query = useMemo(() => ({
    from: currentCompetence(),
    months: 12,
    scenario: "base",
    projectKey,
  }), [projectKey]);
  const surface = useDirectorSurface("receivables-commitments", query);
  const { data } = surface;
  const capabilities = accessByCompany[selectedCompanyId]?.capabilities || [];
  const metadataProject = (metadata.projects || []).find((item) => item.key === projectKey);
  const project = data?.project || null;
  const projectName = project?.name || metadataProject?.name || "Obra";
  const wbsBuildingId = project?.wbsBuildingId || metadataProject?.wbsBuildingId || null;
  const summary = data?.summary || {};
  const covered = Number.isSafeInteger(summary.coverageBps) && summary.coverageBps >= 10000;
  const freshness = oldestExtractedAt(data?.freshness);

  return <ModuleShell
    eyebrow={projectKey ? "Diretoria · Recebíveis x compromissos" : undefined}
    title={projectKey ? projectName : "Recebíveis x compromissos"}
    description={projectKey
      ? `${project?.legalEntityName ? `${project.legalEntityName} · ` : ""}${data
        ? `as entradas previstas desta obra ${covered ? "cobrem" : "não cobrem"} o que ela já assumiu.`
        : "entradas previstas contra os compromissos desta obra."}`
      : "O que deve entrar (carteira e outras receitas) contra o que as obras já assumiram e ainda planejam gastar."}
    badge={`Próximos ${data?.horizon?.months || 12} meses`}
    qualitySources={[surface]}
  >
    {projectKey && <nav className="director-owner-actions" aria-label="Navegação da obra">
      <Link className="director-link-button director-link-button--secondary" to={`${BASE_PATH}${companyQuery(selectedCompanyId)}`} onClick={clearProject}>
        Voltar à carteira
      </Link>
      {wbsBuildingId && <Link className="director-link-button" to={`/dashboard/${encodeURIComponent(wbsBuildingId)}`}>
        Abrir a obra no Ampli Obras
      </Link>}
      {capabilities.includes("director.portfolio.read") && <Link
        className="director-link-button director-link-button--secondary"
        to={`/dashboard/director/centers${companyQuery(selectedCompanyId)}`}
      >
        Ver centros e títulos da obra
      </Link>}
    </nav>}
    <ModuleState
      {...surface}
      availability={data?.status === "no_data" ? "empty" : undefined}
      onRetry={surface.reload}
    />
    {data && data.status !== "no_data" && <>
      <Kpis summary={summary} horizonMonths={data.horizon?.months} project={Boolean(projectKey)} />
      <article className="director-module1__panel">
        <header>
          <div>
            <span className="director-eyebrow">Mês a mês</span>
            <h2>{projectKey ? "Entra x sai, mês a mês" : `Entra x sai, próximos ${data.horizon?.months || 12} meses`}</h2>
          </div>
          <p>O número sobre cada mês é o que sobra ou falta naquele mês.</p>
        </header>
        <CoverageBars months={data.months || []} />
        {!projectKey && <p className="director-owner-footnote">
          Obras em SPEs diferentes não compensam caixa entre si: o consolidado é uma soma para leitura, não uma transferência.
        </p>}
      </article>
      {!projectKey && <ProjectsTable data={data} selectedCompanyId={selectedCompanyId} />}
      {freshness && <footer className="director-freshness">Fontes atualizadas em {formatFreshness(freshness)}.</footer>}
    </>}
  </ModuleShell>;
};

export default ReceivablesCommitments;
